import { ISubscriptionPaymentSummary, SubscriptionType } from 'fitbeat.models';
import React, { Component, PureComponent } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Theme from '../../theme';
import { capitalize } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import { getPerIntervalProRatedBilling } from './PaymentSummaryDisplay';

interface IProps {
  t: (key: string, options?: any) => string;
  total: number;
  hideHeading?: boolean;
  paymentSubscriptionSummary: ISubscriptionPaymentSummary;
}

export const getPerIntervalText = (paymentSubscriptionSummary: ISubscriptionPaymentSummary) => {
  if ((paymentSubscriptionSummary.subscriptionType === SubscriptionType.PayAsYouGym) ||
    (typeof paymentSubscriptionSummary.onceOffPlanAmount === 'number' &&
      paymentSubscriptionSummary.onceOffPlanAmount > 0)) {
    return 'once off';
  }
  if (paymentSubscriptionSummary.intervalCount > 1) {
    return `every ${paymentSubscriptionSummary.intervalCount} ${paymentSubscriptionSummary.interval}s`;
  }
  return `per ${paymentSubscriptionSummary.interval}`;
};

class SubscriptionSummary extends PureComponent<IProps> {

  public couponAmountOff = (coupon: any, currencySymbol: string): string => {
    const { t } = this.props;
    if (coupon.percent_off) {
      return `-${coupon.percent_off.toFixed(0)}% ${t('off')}`;
    }
    return `${currencySymbol}${coupon.amount_off / 100} ${t('off')}`;
  };

  public render() {
    const { t, paymentSubscriptionSummary, hideHeading } = this.props;
    const showPaymentIntervalForCurrentSubscription = !paymentSubscriptionSummary.displayDescription;

    const nextSubscription = paymentSubscriptionSummary.nextSubscription;
    const showPreDiscountAmountOnNextSubscription = nextSubscription &&
      nextSubscription.totalIncludingOnceOffPrices !== nextSubscription.amount;

    return (
      <>
        {!hideHeading &&
          <View style={styles.headingContainer}>
            <Text style={styles.heading}>
              {paymentSubscriptionSummary.paymentMethodRequired ?
                t('paymentScreenHeaderCardRequiredPart1') : t('pressContinueTo')}
            </Text>
            <Text style={styles.headingBold}>{t('paymentScreenHeaderPart2')}</Text>
          </View>
        }

        <View style={styles.row}>
          <View style={styles.rowTextContainer}>
            <Text style={styles.boldReg}>{paymentSubscriptionSummary.name}</Text>
            <Text style={styles.lightText}>{paymentSubscriptionSummary.displayDescription}</Text>
          </View>
          <View style={styles.rowValueContainer}>
            <Text style={styles.boldReg}>{paymentSubscriptionSummary.subscriptionDisplayAmountBeforeDiscounts}</Text>
            {showPaymentIntervalForCurrentSubscription &&
              <Text style={styles.small}>{getPerIntervalText(paymentSubscriptionSummary)}</Text>
            }
          </View>
        </View>

        {paymentSubscriptionSummary.coupon &&
          <View style={styles.row}>
            <View style={styles.rowTextContainer}>
              <Text style={styles.reg}>{t('coupon')}: {paymentSubscriptionSummary.coupon.name}</Text>
            </View>
            <View style={styles.rowValueContainer}>
              <Text style={styles.reg}>
                {this.couponAmountOff(paymentSubscriptionSummary.coupon, paymentSubscriptionSummary.currencySymbol)}
              </Text>
            </View>
          </View>
        }

        {nextSubscription &&
          <>
            <View style={[styles.row, { paddingBottom: defaultScale(10) }]}>
              <View style={styles.rowTextContainer}>
                <Text style={styles.reg}>{t('intervalMembership', {
                  interval: capitalize(
                    getPerIntervalProRatedBilling(t, nextSubscription.interval),
                  ),
                })}</Text>
                <Text style={styles.lightText}>
                  {nextSubscription.asFuturePlanDescription}
                </Text>
              </View>
              <View style={styles.rowValueContainer}>
                <Text style={styles.regLight}>
                  ${nextSubscription.totalIncludingOnceOffPrices}
                </Text>
                {showPreDiscountAmountOnNextSubscription &&
                  <Text style={[styles.small, {
                    textDecorationLine: 'line-through',
                    color: Theme.GREY_FIGMA_MEDIUM_LIGHT,
                  }]}>
                    ${nextSubscription.amount}
                  </Text>
                }
              </View>
            </View>

            {nextSubscription.coupon &&
              <View style={[styles.row, { borderTopWidth: 0, paddingTop: defaultScale(0) }]}>
                <View style={styles.rowTextContainer}>
                  <Text style={styles.regLight}>
                    {t('coupon')}: {nextSubscription.coupon.name}
                  </Text>
                </View>
                <View style={styles.rowValueContainer}>
                  <Text style={styles.regLight}>
                    {this.couponAmountOff(nextSubscription.coupon)}
                  </Text>
                </View>
              </View>
            }
          </>
        }

        <View style={[styles.row, { borderBottomWidth: 1 }]}>
          <View style={styles.rowTextContainer}>
            <Text style={styles.totalText}>{t('totalDueToday')}</Text>
          </View>
          <View style={styles.rowValueContainer}>
            <Text style={styles.totalText}>{paymentSubscriptionSummary.subscriptionDisplayTotalAfterDiscounts}</Text>
            {showPaymentIntervalForCurrentSubscription &&
              <Text style={styles.small}>{getPerIntervalText(paymentSubscriptionSummary)}</Text>
            }
          </View>
        </View>
      </>
    );
  }
}

export const styles = StyleSheet.create({
  headingContainer: {
    marginVertical: defaultScale(20),
  },
  heading: {
    ...Theme.textStyle.display1,
  },
  headingBold: {
    ...Theme.textStyle.display1Bold,
  },
  row: {
    flexDirection: 'row',
    borderColor: Theme.GREY_LIGHT,
    borderTopWidth: 1,
    paddingVertical: defaultScale(18),
  },
  rowTextContainer: {
    flex: 1,
  },
  rowValueContainer: {
    textAlign: 'right',
    alignItems: 'flex-end',
  },
  boldReg: {
    ...Theme.textStyle.bodyCopyBold,
  },
  reg: {
    ...Theme.textStyle.bodyCopyReg,
  },
  regLight: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_FIGMA_MEDIUM_LIGHT,
  },
  lightText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_FIGMA_MEDIUM_LIGHT,
  },
  valueText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  totalText: {
    ...Theme.textStyle.heading2,
  },
  small: {
    ...Theme.textStyle.small,
  },
});

export default SubscriptionSummary;
